import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface PushNotificationState {
  isSupported: boolean;
  permission: NotificationPermission | null;
  isSubscribed: boolean;
}

// VAPID public key placeholder - in production this would come from backend
// For now we use browser-only notifications
const NOTIFICATION_PERMISSION_KEY = 'flaer_notifications_enabled';

export function usePushNotifications() {
  const { user } = useAuth();
  const [state, setState] = useState<PushNotificationState>({
    isSupported: false,
    permission: null,
    isSubscribed: false,
  });
  const [isLoading, setIsLoading] = useState(false);

  // Check if notifications are supported
  useEffect(() => {
    const isSupported = 'Notification' in window;
    const storedPreference = localStorage.getItem(NOTIFICATION_PERMISSION_KEY);
    
    setState({
      isSupported,
      permission: isSupported ? Notification.permission : null,
      isSubscribed: storedPreference === 'true' && Notification.permission === 'granted',
    });
  }, []);

  // Request notification permission
  const requestPermission = useCallback(async () => {
    if (!state.isSupported) {
      toast.error('Notifications are not supported in this browser');
      return false;
    }

    setIsLoading(true);
    try {
      const permission = await Notification.requestPermission();
      
      const granted = permission === 'granted';
      localStorage.setItem(NOTIFICATION_PERMISSION_KEY, String(granted));
      
      setState(prev => ({
        ...prev,
        permission,
        isSubscribed: granted,
      }));

      if (granted) {
        toast.success('Notifications enabled!', {
          description: 'You\'ll receive alerts for price triggers and trade signals',
        });
        
        // Show a test notification
        showNotification('FLAER Notifications Enabled', {
          body: 'You\'ll now receive real-time alerts for price triggers and trade signals.',
          icon: '/flaer-icon-192.png',
          tag: 'welcome',
        });
      } else {
        toast.error('Notification permission denied');
      }

      return granted;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      toast.error('Failed to enable notifications');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [state.isSupported]);

  // Show a browser notification
  const showNotification = useCallback((title: string, options?: NotificationOptions) => {
    if (!state.isSupported || Notification.permission !== 'granted') {
      console.log('Notifications not available, showing toast instead');
      toast(title, { description: options?.body });
      return;
    }

    try {
      const notification = new Notification(title, {
        icon: '/flaer-icon-192.png',
        badge: '/flaer-icon-192.png',
        requireInteraction: false,
        silent: false,
        ...options,
      });

      notification.onclick = () => {
        window.focus();
        notification.close();
        
        // Navigate based on tag
        if (options?.tag?.includes('signal')) {
          window.location.href = '/markets';
        } else if (options?.tag?.includes('trade')) {
          window.location.href = '/trades';
        } else if (options?.tag?.includes('price')) {
          window.location.href = '/watchlist';
        }
      };

      // Auto-close after 10 seconds
      setTimeout(() => notification.close(), 10000);
    } catch (error) {
      console.error('Error showing notification:', error);
      toast(title, { description: options?.body });
    }
  }, [state.isSupported]);

  // Notify about price alert
  const notifyPriceAlert = useCallback((alert: {
    symbol: string;
    target_price: number;
    current_price: number;
    condition: string;
  }) => {
    showNotification(`🔔 Price Alert: ${alert.symbol}`, {
      body: `${alert.symbol} has ${alert.condition === 'above' ? 'risen above' : 'dropped below'} ₹${alert.target_price.toFixed(2)}. Current: ₹${alert.current_price.toFixed(2)}`,
      tag: `price-alert-${alert.symbol}`,
      data: { type: 'price_alert', ...alert },
    });
  }, [showNotification]);

  // Notify about new signal
  const notifyNewSignal = useCallback((signal: {
    symbol: string;
    direction: string;
    strategy: string;
    entry_price: number;
    confluence_score?: number;
  }) => {
    const emoji = signal.direction === 'LONG' ? '📈' : '📉';
    showNotification(`${emoji} New Signal: ${signal.symbol}`, {
      body: `${signal.direction} @ ₹${signal.entry_price} (${signal.strategy.replace('_', ' ')}) - Confluence: ${signal.confluence_score || 3}/5`,
      tag: `signal-${signal.symbol}-${Date.now()}`,
      data: { type: 'signal', ...signal },
    });
  }, [showNotification]);

  // Notify about trade update
  const notifyTradeUpdate = useCallback((trade: {
    symbol: string;
    status: 'opened' | 'closed';
    pnl?: number;
    pnl_percent?: number;
    entry_price?: number;
    exit_price?: number;
    quantity?: number;
  }) => {
    if (trade.status === 'opened') {
      showNotification(`💼 Trade Opened: ${trade.symbol}`, {
        body: `Bought ${trade.quantity} @ ₹${trade.entry_price}`,
        tag: `trade-open-${trade.symbol}`,
        data: { type: 'trade', ...trade },
      });
    } else {
      const isProfit = (trade.pnl ?? 0) > 0;
      const emoji = isProfit ? '🎉' : '📉';
      showNotification(`${emoji} Trade Closed: ${trade.symbol}`, {
        body: `${isProfit ? 'Profit' : 'Loss'}: ₹${Math.abs(trade.pnl || 0).toFixed(2)} (${(trade.pnl_percent ?? 0) >= 0 ? '+' : ''}${(trade.pnl_percent ?? 0).toFixed(2)}%)`,
        tag: `trade-close-${trade.symbol}`,
        data: { type: 'trade', ...trade },
      });
    }
  }, [showNotification]);

  // Notify about risk warning
  const notifyRiskWarning = useCallback((warning: {
    type: string;
    message: string;
    severity: 'low' | 'medium' | 'high';
  }) => {
    const emoji = warning.severity === 'high' ? '🚨' : warning.severity === 'medium' ? '⚠️' : 'ℹ️';
    showNotification(`${emoji} Risk Alert`, {
      body: warning.message,
      tag: `risk-${warning.type}`,
      requireInteraction: warning.severity === 'high',
      data: { type: 'risk_warning', ...warning },
    });
  }, [showNotification]);

  // Disable notifications
  const disableNotifications = useCallback(() => {
    localStorage.setItem(NOTIFICATION_PERMISSION_KEY, 'false');
    setState(prev => ({ ...prev, isSubscribed: false }));
    toast.info('Notifications disabled');
  }, []);

  return {
    isSupported: state.isSupported,
    permission: state.permission,
    isSubscribed: state.isSubscribed,
    isLoading,
    requestPermission,
    disableNotifications,
    showNotification,
    notifyPriceAlert,
    notifyNewSignal,
    notifyTradeUpdate,
    notifyRiskWarning,
  };
}
